/**
 * PaymentDateInput — data do pagamento com horário OPCIONAL.
 *
 * A data usa o `DateField` do sistema (input mascarado dd/mm/aaaa + calendário), o mesmo das
 * demais telas — em vez do `input[type=date]` cru de antes. O horário começa oculto (00:00) e só
 * aparece quando o usuário clica em "+ horário". O backend guarda timestamptz; o valor é convertido
 * de wall-clock local para UTC em `toIsoInstant`, sem risco de D±1.
 */
import { useId } from 'react';
import DateField from '../DateField';
import InputFrame from '../../atoms/InputFrame';

interface Props {
    date: string;                          // "YYYY-MM-DD"
    time: string;                          // "HH:MM"
    showTime: boolean;
    onChange: (next: { date: string; time: string; showTime: boolean }) => void;
    disabled?: boolean;
    className?: string;
    /** rótulo do campo de data (o `DateField` do sistema exige um). */
    label?: string;
    /** prefixo dos ids da data e do horário; sem ele, um único por instância (`useId`). */
    id?: string;
}

/** Junta data + hora locais no instante ISO (UTC) que o backend espera. */
export const toIsoInstant = (date: string, time: string, showTime: boolean): string | undefined => {
    if (!date) return undefined;
    return new Date(`${date}T${showTime && time ? time : '00:00'}`).toISOString();
};

const TIME_INPUT_CLASS = 'block py-2.5 px-0 w-full text-sm text-gray-900 dark:text-white bg-transparent border-0 border-b-2 border-gray-300 appearance-none focus:outline-none focus:ring-0 focus:border-blue-600 dark:focus:border-blue-400 peer disabled:opacity-50 disabled:cursor-not-allowed';

const PaymentDateInput: React.FC<Props> = ({ date, time, showTime, onChange, disabled, className, label = 'Data do Pagamento', id }) => {
    const generated = useId();
    const baseId = id ?? `payment-${generated}`;
    const dateId = `${baseId}-date`;
    const timeId = `${baseId}-time`;
    return (
        <div className={`flex gap-2 items-start ${className ?? ''}`}>
            <div className="flex-1">
                <DateField
                    id={dateId}
                    label={label}
                    value={date}
                    disabled={disabled}
                    onChange={iso => onChange({ date: iso, time, showTime })}
                />
            </div>
            {showTime ? (
                <div className="w-28">
                    {/* `id`/`htmlFor` de verdade: sem eles o rótulo flutuante é texto solto ao lado
                        do campo — leitor de tela não anuncia o nome, e o teste só alcança o input
                        varrendo o DOM por `input[type=time]`. */}
                    <InputFrame label="Horário" htmlFor={timeId} className="mb-4">
                        <input type="time" id={timeId} value={time} disabled={disabled}
                            className={TIME_INPUT_CLASS}
                            onChange={e => onChange({ date, time: e.target.value, showTime })} />
                    </InputFrame>
                </div>
            ) : (
                <button type="button" disabled={disabled}
                    onClick={() => onChange({ date, time, showTime: true })}
                    className="text-xs text-blue-600 whitespace-nowrap hover:underline disabled:opacity-50 mt-3">+ horário</button>
            )}
        </div>
    );
};

export default PaymentDateInput;
